import { ref, computed } from 'vue'
import { useMediaQuery } from '@vueuse/core'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

// État partagé entre tous les consommateurs : un seul couple d'écouteurs
// suffit pour toute l'app, et plusieurs composants peuvent observer le
// même état (About + nudge d'onboarding par exemple).
const installEvent = ref<BeforeInstallPromptEvent | null>(null)
const installed = ref(false)
let listenersAttached = false

function attachListeners() {
  if (listenersAttached || typeof window === 'undefined') return
  listenersAttached = true
  window.addEventListener('beforeinstallprompt', evt => {
    evt.preventDefault()
    installEvent.value = evt as BeforeInstallPromptEvent
  })
  window.addEventListener('appinstalled', () => {
    installed.value = true
    installEvent.value = null
  })
}

export default function useInstallApp() {
  attachListeners()
  const isStandalone = useMediaQuery('(display-mode: standalone)')

  const canInstall = computed(
    () => !installed.value && !isStandalone.value && installEvent.value !== null
  )

  async function prompt(): Promise<void> {
    if (!installEvent.value) return
    await installEvent.value.prompt()
    installEvent.value = null
  }

  return {
    canInstall,
    isStandalone,
    prompt,
    // Alias conservé pour AboutView.vue qui appelle directement
    // `@click.prevent="promptStorage"` ; remplacable progressivement.
    promptStorage: prompt
  }
}
